/**
 * useDocumentRenderField — silnik formatek dokumentów (pilot: klasy gołe
 * commercial / internal).
 *
 * Po keystone (unifikacja-formatek, Krok 1) to CIENKI wrapper nad generycznym
 * silnikiem `useKartotekaRenderField`: buduje `KartotekaEngineConfig` klasy
 * gołej (`buildDocumentEngineConfig`) i deleguje. Publiczna sygnatura wrappera
 * jest NIEZMIENIONA (`{ docClass, form, onChange, extValues, onExtChange,
 * editable, isCreate }`), więc strony pilota (CommercialDocDetail /
 * InternalDocDetail) i testy RTL działają bez zmian — dowód addytywności (Z2).
 *
 * Cała logika renderowania (routing core/ext, fallback 403, indeks slug+id,
 * koercja) żyje teraz w silniku; różnice pilota (etykiety i18n, testid'y,
 * nadpisania widgetów) niesie config — patrz `documentEngineConfig.tsx`.
 *
 * Testid'y core są 1:1 z dawnym `BareDocDataTab`
 * (`${docClass}-doc-field-${stateKey}`) — zależą od nich testy atomic-save
 * i TC stron pilota.
 */

import { type ReactNode, useMemo } from 'react';
import type { DocumentTypeFieldState } from '@saas/shared';

import type { BareDocFormData } from '@/components/documents/documentCoreRenderers';
import { isSlugMatch } from '@/components/kartoteki/engine/engine-config';
import type { EngineFormField } from '@/components/kartoteki/engine/engine-types';
import { useKartotekaRenderField } from '@/components/kartoteki/engine/useKartotekaRenderField';

import { buildDocumentEngineConfig } from './documentEngineConfig';

export interface UseDocumentRenderFieldArgs {
  /** Klasa dokumentu = moduleSlug dla GET /system-modules/:slug/fields. */
  docClass: 'commercial' | 'internal' | 'specjalne';
  form: BareDocFormData;
  /** Aktualizacja stanu nagłówka (= setForm strony). */
  onChange: (next: BareDocFormData) => void;
  /** Stan wartości pól dodanych (slug → wartość), kontrolowany przez stronę. */
  extValues: Record<string, unknown>;
  /** Aktualizacja pojedynczej wartości pola dodanego. */
  onExtChange: (slug: string, value: unknown) => void;
  /** Czy formularz jest w trybie edycji. */
  editable: boolean;
  /** Tryb tworzenia — steruje placeholderem numeru systemowego. */
  isCreate: boolean;
  /**
   * Firma dla scopingu pickerów zależnych od firmy (T3: `owner_user_id` →
   * `UserPicker`, mirror `companyId` na fakturach/umowach/pismach — WSZYSTKIE
   * kartoteki dokumentowe mają teraz ten sam kontrakt, [E] domknął ostatnią
   * lukę na fakturach w edit). Create: firma z kreatora typu dokumentu (z URL);
   * edit: firma zapisana na rekordzie (`detailQ.data.companyId`). `undefined`/
   * `null` = brak kontekstu firmy (picker nieograniczony, CLAUDE.md §4.8
   * boundary rule).
   */
  companyId?: string | null;
  /**
   * Slug pola daty numeracji wybranego typu (znormalizowany) — pole o tym slugu dostaje
   * gwiazdkę „wymagane". `undefined` = brak numeracji po polu → zero gwiazdek numeracyjnych.
   */
  numberingBaseFieldSlug?: string;
  /**
   * Stan pól dodatkowych w typie dokumentu (`useDocumentTypeFields`): `excluded` ukrywa pole,
   * `required` zapala gwiazdkę. `null`/brak → zachowanie bez typu.
   */
  typeFieldStates?: ReadonlyMap<string, DocumentTypeFieldState> | null;
  /** Stany pól typu są jeszcze wczytywane — pola dodatkowe pokazują szkielet (patrz silnik formatek). */
  typeFieldsPending?: boolean;
  /** Podświetlenie pola odrzuconego przez serwer (`VALIDATION_ERROR.details`) — opcjonalne, addytywne. */
  hasError?: (key: keyof BareDocFormData) => boolean;
  /** Komunikat błędu pod polem (czerwony tekst) — analogiczny do `hasError`, zwraca tekst. */
  getError?: (key: keyof BareDocFormData) => string | undefined;
  /** Czyszczenie błędu pola przy edycji (wołane w onChange przez silnik). */
  clearError?: (key: keyof BareDocFormData) => void;
}

export interface UseDocumentRenderFieldResult {
  renderField: (fieldId: string, settings?: Record<string, unknown>) => ReactNode;
  fields: EngineFormField[];
  isLoading: boolean;
}

export function useDocumentRenderField({
  docClass,
  form,
  onChange,
  extValues,
  onExtChange,
  editable,
  isCreate,
  companyId,
  numberingBaseFieldSlug,
  typeFieldStates,
  typeFieldsPending,
  hasError,
  getError,
  clearError,
}: UseDocumentRenderFieldArgs): UseDocumentRenderFieldResult {
  const config = useMemo(() => buildDocumentEngineConfig(docClass), [docClass]);
  return useKartotekaRenderField(config, {
    form,
    onChange,
    extValues,
    onExtChange,
    editable,
    isCreate,
    companyId,
    isFieldExtraRequired: isSlugMatch(numberingBaseFieldSlug),
    typeFieldStates,
    typeFieldsPending,
    hasError,
    getError,
    clearError,
  });
}
